import React, { useState } from 'react';
import {
  X,
  Store,
  Zap,
  AlertTriangle,
  Copy,
  Check,
  Sparkles,
  MapPin,
  Flame,
  Info,
} from 'lucide-react';
import { Language } from '../types/spot';

interface VeggieSnacksModalProps {
  isOpen: boolean;
  onClose: () => void;
  language: Language;
  onCopyToast?: (text: string) => void;
}

interface SnackItem {
  id: string;
  emoji: string;
  titleDe: string;
  titleEn: string;
  titleKr: string;
  hangul: string;
  calories: string;
  protein: string;
  locationDe: string;
  locationEn: string;
  locationKr: string;
  descDe: string;
  descEn: string;
  descKr: string;
}

const SNACKS: SnackItem[] = [
  {
    id: 'eggs',
    emoji: '🥚',
    titleDe: 'Geröstete Eier (Guun Gyeran)',
    titleEn: 'Roasted & Smoked Eggs',
    titleKr: '구운계란 / 훈제란',
    hangul: '구운계란',
    calories: '~140–160 kcal (2 Stk.)',
    protein: '12–14g Protein',
    locationDe: 'Frischetheke / Snackregal (2er- oder 3er-Pappschalen)',
    locationEn: 'Fresh refrigerated / snack shelf (2 or 3 pack cartons)',
    locationKr: '신선식품/간식 코너 (2~3개입 종이 트레이 포장)',
    descDe:
      'Braune Schale, wunderbar rauchig-aromatisch und deutlich fester als normale Eier. Extrem leicht zu pellen, 100 % vegetarisch (Ovo), liefert sofort hochwertiges Eiweiß und Fett.',
    descEn:
      'Brown-shelled, slow-baked eggs with a rich smoky flavor. Super easy to peel on the go, 100% vegetarian friendly, rich in protein and healthy fats.',
    descKr:
      '갈색 껍질의 쫄깃하고 깊은 풍미를 지닌 구운계란. 껍질이 잘 벗겨져 이동 중 간편하게 섭취 가능하며 양질의 단백질과 영양을 채워줍니다.',
  },
  {
    id: 'nuts',
    emoji: '🥜',
    titleDe: 'Tages-Nussmischungen (Haru Gyeon-gwa)',
    titleEn: 'Daily Mixed Nut Packs (Haru Gyeongwa)',
    titleKr: '하루견과',
    hangul: '하루견과',
    calories: '~140–170 kcal / Beutel',
    protein: 'Gesunde Fette & Energie',
    locationDe: 'Kassenbereich & Snack-Ständer',
    locationEn: 'Near checkout register & nut snack section',
    locationKr: '카운터 주변 및 견과류 매대',
    descDe:
      'Kompakte Portionsbeutel mit Mandeln, Walnüssen, Cashews und Cranberries. Extrem hohe Kaloriendichte bei minimalem Volumen – steck dir immer 2–3 Beutel als Energiereserve in die Tasche!',
    descEn:
      'Handy single-serving packs of almonds, walnuts, cashews, and cranberries. Highest calorie density with zero bulk – pack 2-3 bags in your daypack to prevent energy dips.',
    descKr:
      '아몬드, 호두, 캐슈넛, 건크랜베리가 한 봉지에 든 영양 간식. 부피 대비 칼로리 밀도가 매우 높아 배낭에 2~3개씩 상비하기에 최고입니다.',
  },
  {
    id: 'cheese',
    emoji: '🧀',
    titleDe: 'Käsesticks (String Cheese)',
    titleEn: 'String Cheese Sticks',
    titleKr: '스트링치즈',
    hangul: '스트링치즈',
    calories: '~65–80 kcal / Stick',
    protein: '5–6g Protein & Fett',
    locationDe: 'Kühlregal bei Wurst & Milchprodukten',
    locationEn: 'Chilled dairy section',
    locationKr: '냉장 유제품/육가공 코너',
    descDe:
      'Einzeln verschweißte Mozzarella-/Käsesticks. Geheimtipp: Für 7 bis 10 Sekunden in die Store-Mikrowelle legen – danach warm, cremig, herzhaft und voller Energie.',
    descEn:
      'Individually wrapped string cheese. Pro tip: Microwave for 7-10 seconds inside the store for melted, warm, high-fat savory fuel.',
    descKr:
      '개별 진공 포장된 쫄깃한 스트링치즈. 매장 내 전자레인지에 7~10초 살짝 데우면 따뜻하고 부드러운 고소함을 즐길 수 있습니다.',
  },
  {
    id: 'soymilk',
    emoji: '🥛',
    titleDe: 'Sojamilch & Protein-Drinks (Vegemil)',
    titleEn: 'Soy Milk & Ready Protein RTDs',
    titleKr: '두유 & 단백질 음료 (베지밀)',
    hangul: '베지밀 B',
    calories: '~120–160 kcal / Flasche',
    protein: '7–20g Protein',
    locationDe: 'Getränkekühlung & Warmhalte-Glasvitrine',
    locationEn: 'Refrigerated drinks & heated warmers in winter',
    locationKr: '음료 냉장고 및 겨울철 온장고',
    descDe:
      'Flüssige Kalorien füllen das Defizit am schnellsten: Vegemil B (달콤한 맛 - süß & gehaltvoll) oder schwarze Bohnen-Sojamilch (검은콩 두유). Für maximales Eiweiß: Fertigshakes wie "The Danbaek (더단백)" oder "Selex (셀렉스)" (Milchbasis).',
    descEn:
      'Liquid calories are the quickest way to beat deficits: Vegemil B (sweeter, energy-rich) or black bean soy milk. For max protein, look for RTDs like "The Danbaek" or "Selex" (dairy-based).',
    descKr:
      '빠른 에너지 보충엔 마시는 칼로리가 가장 효과적입니다. 달콤한 베지밀 B나 고소한 검은콩 두유, 고단백 보충엔 더단백·셀렉스 완제품을 추천합니다.',
  },
  {
    id: 'chestnuts',
    emoji: '🌰',
    titleDe: 'Gegarte Kastanien (Matbam) & Süßkartoffeln',
    titleEn: 'Steamed Chestnuts (Matbam) & Baked Sweet Potato',
    titleKr: '맛밤 & 군고구마',
    hangul: '맛밤',
    calories: '~130–250 kcal',
    protein: 'Komplexe Kohlenhydrate',
    locationDe: 'CJ Matbam im Snackregal; Gun-goguma warm am Tresen',
    locationEn: 'CJ Matbam on snack shelves; hot Gun-goguma at checkout',
    locationKr: '스낵 코너 (CJ 맛밤) 및 카운터 군고구마 온장고',
    descDe:
      'CJ Matbam (맛밤) sind pure, weiche Esskastanien ohne Zusätze – leicht verdauliche Carbs. Im Herbst/Winter stehen in fast jedem GS25/CU warme gebackene Süßkartoffeln (군고구마) direkt an der Kasse.',
    descEn:
      'CJ Matbam are 100% steamed peeled sweet chestnuts with wholesome complex carbs. In autumn/winter, look for piping-hot roasted sweet potatoes (Gun-goguma) at the counter.',
    descKr:
      '순수 100% 쪄낸 부드러운 CJ 맛밤과, 가을·겨울철 편의점 카운터 옆 온장고에서 바로 꺼내주는 꿀 군고구마는 든든한 천연 탄수화물원입니다.',
  },
  {
    id: 'tofu',
    emoji: '🌱',
    titleDe: 'Snack-Tofu-Riegel (Dubu-ba)',
    titleEn: 'Savory Tofu Bars (Dubu-ba)',
    titleKr: '풀무원 두부바',
    hangul: '두부바',
    calories: '~90–110 kcal / Riegel',
    protein: '9–10g Pflanzl. Protein',
    locationDe: 'Kühlregal bei Fertigmahlzeiten & Salaten',
    locationEn: 'Chilled ready-to-eat & salad section',
    locationKr: '냉장 샐러드/간편식 코너',
    descDe:
      'Z. B. von Pulmuone. Schnittfester, würzig marinierter Snack-Tofu (Kräuter oder Teriyaki). Direkt aus der Packung kalt verzehrbar – rein pflanzliche Eiweißbombe.',
    descEn:
      'Made by brands like Pulmuone: firm, savory seasoned tofu bars (herbs, plain, teriyaki). Ready to eat straight from the pack, 100% plant-based.',
    descKr:
      '풀무원 등에서 선보이는 한 손에 쏙 들어오는 간편 두부바. 데우지 않고 바로 섭취할 수 있는 순식물성 고단백 웰빙 간식입니다.',
  },
];

const PHRASES = [
  { kr: '구운계란', de: 'Geröstete Eier', en: 'Roasted Eggs' },
  { kr: '하루견과', de: 'Tages-Nussmix', en: 'Daily Nut Pack' },
  { kr: '베지밀 B', de: 'Vegemil B (Süße Sojamilch)', en: 'Vegemil B (Sweet Soy Milk)' },
  { kr: '검은콩 두유', de: 'Schwarze Bohnen Sojamilch', en: 'Black Bean Soy Milk' },
  { kr: '스트링치즈', de: 'Käsestick', en: 'String Cheese' },
  { kr: '맛밤', de: 'Gegarte Kastanien', en: 'Sweet Chestnuts' },
  { kr: '군고구마', de: 'Gebackene Süßkartoffel', en: 'Roasted Sweet Potato' },
  { kr: '두부바', de: 'Tofu-Riegel', en: 'Tofu Bar' },
  { kr: '비건 식품', de: 'Vegane Lebensmittel', en: 'Vegan Foods' },
  { kr: '고기 빼주세요', de: 'Bitte ohne Fleisch', en: 'No meat, please' },
];

export const VeggieSnacksModal: React.FC<VeggieSnacksModalProps> = ({
  isOpen,
  onClose,
  language,
  onCopyToast,
}) => {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(text);
    setTimeout(() => setCopiedKey(null), 2000);
    if (onCopyToast) {
      const msg =
        language === 'kr'
          ? `"${text}" 복사 완료!`
          : language === 'en'
          ? `Copied "${text}" to clipboard!`
          : `"${text}" in die Zwischenablage kopiert!`;
      onCopyToast(msg);
    }
  };

  const t = {
    de: {
      title: '24/7 Convenience Store Veggie-Snacks',
      subtitle: 'Schnelle Kalorien & Protein gegen Gewichtsverlust in Korea (CU, GS25, 7-Eleven)',
      comboBadge: '⚡ 2-Minuten-Notfall-Kombi',
      comboCal: '~450–550 kcal',
      comboFormula: '1 Flasche Vegemil B + 2 geröstete Eier (Guun Gyeran) + 1 Packung Tagesnüsse',
      comboDesc:
        'Der schnellste Weg, das tägliche Lauf-Defizit auszugleichen: Überall in 2 Minuten ohne Kochen erhältlich, sättigt und stoppt den Gewichtsverlust zuverlässig.',
      whereLabel: 'Wo im Store:',
      warningTitle: 'Wichtige Fallen für Vegetarier im Store',
      warn1:
        'Dreiecks-Kimbap (삼각김밥): Selbst Varianten wie Kimchi- oder Gemüsekimbap enthalten fast ausnahmslos Thunfisch, Schweinefleisch oder Rinderbrühe (Dashida). Nur kaufen, wenn explizit ein grünes "비건 (Vegan)" oder "채식" Label aufgedruckt ist!',
      warn2:
        'Chips & Snack-Cracker: Fast alle würzigen Kartoffelchips und Maissnacks enthalten Rindfleischextrakt (소고기분말), Hühnerpulver oder Garnelenpulver. Sicherer sind naturbelassene Nüsse, Kastanien oder süße Reiswaffeln.',
      cheatsheetTitle: 'Spickzettel für das Smartphone (1-Klick zum Kopieren & Vorzeigen)',
      copyTooltip: 'Koreanischen Begriff kopieren',
      close: 'Schließen',
    },
    en: {
      title: '24/7 Convenience Store Veggie Snacks',
      subtitle: 'Quick calories & protein to maintain weight while traveling Korea (CU, GS25, 7-Eleven)',
      comboBadge: '⚡ 2-Minute Emergency Combo',
      comboCal: '~450–550 kcal',
      comboFormula: '1 Bottle Vegemil B + 2 Roasted Eggs (Guun Gyeran) + 1 Daily Nut Pack',
      comboDesc:
        'The fastest way to counter unexpected walking calorie burn: Available everywhere 24/7 with zero prep, packed with protein and wholesome fats.',
      whereLabel: 'Where in Store:',
      warningTitle: 'Important Convenience Store Traps for Vegetarians',
      warn1:
        'Triangle Kimbap (삼각김밥): Even kimchi or veggie flavors almost always hide beef bouillon (Dashida), tuna or pork flakes. Only purchase if explicitly tagged with a green "비건 (Vegan)" or "Plant-based" label!',
      warn2:
        'Savory Crisps & Chips: Virtually all seasoned potato/corn chips contain beef powder (소고기분말), chicken stock or shrimp extracts. Stick to plain salted nuts, steamed chestnuts, or sweet rice crackers.',
      cheatsheetTitle: 'Korean Screen Cheatsheet (1-Click Copy & Show to Cashier)',
      copyTooltip: 'Copy Korean phrase',
      close: 'Close',
    },
    kr: {
      title: '24/7 편의점 채식 간식 & 칼로리 가이드',
      subtitle: '한국 여행 중 급격한 체중 감소를 막는 고칼로리·고단백 비건 & 채식 간식 (CU · GS25 · 세븐일레븐)',
      comboBadge: '⚡ 2분 급속 칼로리 보충 세트',
      comboCal: '~450–550 kcal',
      comboFormula: '베지밀 B 1병 + 구운계란 2알 + 하루견과 1봉',
      comboDesc:
        '여행 중 많은 도보량으로 인한 칼로리 소모를 빠르게 만회하는 조합. 전국 편의점에서 조리 없이 2분 만에 든든하게 섭취할 수 있습니다.',
      whereLabel: '매장 위치:',
      warningTitle: '편의점 채식 주의사항 & 함정',
      warn1:
        '삼각김밥 주의: 김치나 야채 맛이라도 소고기다시다, 참치, 돼지고기 추출물이 함유된 경우가 대부분입니다. 패키지에 "비건" 또는 "채식" 표기가 명시된 제품만 선택하세요.',
      warn2:
        '스낵류 시즈닝 주의: 짭짤한 감자칩과 스낵류에는 소고기분말, 치킨파우더, 새우엑기스가 포함되어 있으므로 원재료명을 꼼꼼히 확인하세요.',
      cheatsheetTitle: '스마트폰 빠른 복사 & 점원 제시용 한국어 단어장',
      copyTooltip: '한국어 단어 복사하기',
      close: '닫기',
    },
  }[language];

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={t.title}
    >
      <div
        className="modal-sheet veggie-modal-sheet"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="modal-header">
          <div className="modal-header__left">
            <div className="modal-header__icon veggie-modal-header-icon">
              <Store style={{ width: 20, height: 20 }} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <h3
                  style={{
                    fontSize: 16,
                    fontWeight: 800,
                    color: 'var(--text-primary)',
                    margin: 0,
                  }}
                >
                  {t.title}
                </h3>
              </div>
              <p
                style={{
                  fontSize: 12,
                  color: 'var(--text-secondary)',
                  margin: '2px 0 0',
                }}
              >
                {t.subtitle}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="modal-close"
            title={t.close}
            aria-label={t.close}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          {/* Emergency Combo Highlight */}
          <div className="veggie-emergency-card">
            <div className="veggie-emergency-card__header">
              <span className="veggie-emergency-card__badge">
                <Flame style={{ width: 15, height: 15 }} />
                <span>{t.comboBadge}</span>
              </span>
              <span className="veggie-emergency-card__cal">{t.comboCal}</span>
            </div>
            <div className="veggie-emergency-card__formula">
              {t.comboFormula}
            </div>
            <p className="veggie-emergency-card__desc">{t.comboDesc}</p>
          </div>

          {/* Snacks Grid */}
          <div className="veggie-snack-grid">
            {SNACKS.map((snack) => {
              const title =
                language === 'kr'
                  ? snack.titleKr
                  : language === 'en'
                  ? snack.titleEn
                  : snack.titleDe;
              const desc =
                language === 'kr'
                  ? snack.descKr
                  : language === 'en'
                  ? snack.descEn
                  : snack.descDe;
              const loc =
                language === 'kr'
                  ? snack.locationKr
                  : language === 'en'
                  ? snack.locationEn
                  : snack.locationDe;
              const isCopied = copiedKey === snack.hangul;

              return (
                <div key={snack.id} className="veggie-snack-card">
                  <div className="veggie-snack-card__top">
                    <div className="veggie-snack-card__identity">
                      <span className="veggie-snack-card__emoji">
                        {snack.emoji}
                      </span>
                      <div className="veggie-snack-card__names">
                        <span className="veggie-snack-card__title">
                          {title}
                        </span>
                        <span className="veggie-snack-card__kr">
                          {snack.hangul}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleCopy(snack.hangul, title)}
                      className="btn btn--ghost btn--sm"
                      style={{ padding: '4px 8px', fontSize: 11 }}
                      title={t.copyTooltip}
                    >
                      {isCopied ? (
                        <Check style={{ width: 12, height: 12, color: 'var(--success)' }} />
                      ) : (
                        <Copy style={{ width: 12, height: 12 }} />
                      )}
                      <span>{isCopied ? 'Kopiert' : 'Hangul'}</span>
                    </button>
                  </div>

                  <div className="veggie-snack-card__metrics">
                    <span className="veggie-metric-chip veggie-metric-chip--highlight">
                      {snack.calories}
                    </span>
                    <span className="veggie-metric-chip">
                      {snack.protein}
                    </span>
                  </div>

                  <p className="veggie-snack-card__desc">{desc}</p>

                  <div className="veggie-snack-card__location">
                    <MapPin style={{ width: 12, height: 12, flexShrink: 0 }} />
                    <span>
                      <strong>{t.whereLabel}</strong> {loc}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Warnings Card */}
          <div className="veggie-warning-card">
            <div className="veggie-warning-card__header">
              <AlertTriangle style={{ width: 17, height: 17 }} />
              <span>{t.warningTitle}</span>
            </div>
            <ul className="veggie-warning-list">
              <li className="veggie-warning-item">{t.warn1}</li>
              <li className="veggie-warning-item">{t.warn2}</li>
            </ul>
          </div>

          {/* Cheatsheet / Phrase helper */}
          <div className="veggie-cheatsheet">
            <div className="veggie-cheatsheet__title">
              <Sparkles style={{ width: 15, height: 15, color: '#34d399' }} />
              <span>{t.cheatsheetTitle}</span>
            </div>
            <div className="veggie-cheatsheet__grid">
              {PHRASES.map((p) => {
                const meaning = language === 'en' ? p.en : p.de;
                const isCopied = copiedKey === p.kr;
                return (
                  <button
                    key={p.kr}
                    onClick={() => handleCopy(p.kr, meaning)}
                    className="veggie-phrase-btn"
                    title={t.copyTooltip}
                  >
                    <div className="veggie-phrase-btn__texts">
                      <span className="veggie-phrase-btn__kr">{p.kr}</span>
                      <span className="veggie-phrase-btn__meaning">
                        {language === 'kr' ? '' : meaning}
                      </span>
                    </div>
                    {isCopied ? (
                      <Check style={{ width: 13, height: 13, color: '#34d399' }} />
                    ) : (
                      <Copy style={{ width: 13, height: 13, color: 'var(--text-muted)' }} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
